import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import PersonalInfo from './PersonalInfo';
import TechnicalSkills from './TechnicalSkills';
import WorkExperience from './WorkExperience';
import Education from './Education';
import Certifications from './Certifications';
import Projects from './Projects';
import ResumePreview from './ResumePreview';
import ResumeManager from './ResumeManager';
import AddSkillModal from './AddSkillModal';
import AddProjectModal from './AddProjectModal';
import EditSkillModal from './EditSkillModal';
import EditProjectModal from './EditProjectModal';
import EditEducationModal from './EditEducationModal';
import EditWorkExperienceModal from './EditWorkExperienceModal';
import EditCertificationModal from './EditCertificationModal';
import html2pdf from 'html2pdf.js';
import AddEducationModal from './AddEducationModal';
import AddWorkExperienceModal from './AddWorkExperienceModal';
import AddCertificationModal from './AddCertificationModal';
import SuccessModal from './SuccessModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const ResumeBuilder = ({ resumeToEdit, onBackToList, headerTitle, headerDescription }) => {
  const [resumeData, setResumeData] = useState({
    name: '',
    mobile: '',
    email: '',
    profileSummary: '',
    linkedin: '',
    github: '',
    workExperiences: [],
    educations: [],
    certifications: [],
    technicalSkillIds: [],
    projectIds: []
  });

  const [technicalSkills, setTechnicalSkills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isResumeManagerMode, setIsResumeManagerMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [isAddSkillModalOpen, setIsAddSkillModalOpen] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [isEditSkillModalOpen, setIsEditSkillModalOpen] = useState(false);
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [isEditEducationModalOpen, setIsEditEducationModalOpen] = useState(false);
  const [isEditWorkExperienceModalOpen, setIsEditWorkExperienceModalOpen] = useState(false);
  const [isEditCertificationModalOpen, setIsEditCertificationModalOpen] = useState(false);
  const [skillToEdit, setSkillToEdit] = useState(null);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [educationToEdit, setEducationToEdit] = useState(null);
  const [experienceToEdit, setExperienceToEdit] = useState(null);
  const [certificationToEdit, setCertificationToEdit] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAddEducationModalOpen, setIsAddEducationModalOpen] = useState(false);
  const [isAddWorkExperienceModalOpen, setIsAddWorkExperienceModalOpen] = useState(false);
  const [isAddCertificationModalOpen, setIsAddCertificationModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchMasterData();
    
    // Load resume data if editing
    if (resumeToEdit) {
      setResumeData({
        id: resumeToEdit.id || '',
        name: resumeToEdit.name || '',
        mobile: resumeToEdit.mobile || '',
        email: resumeToEdit.email || '',
        profileSummary: resumeToEdit.profileSummary || '',
        linkedin: resumeToEdit.linkedin || '',
        github: resumeToEdit.github || '',
        workExperiences: resumeToEdit.workExperiences || [],
        educations: resumeToEdit.educations || [],
        certifications: resumeToEdit.certifications || [],
        technicalSkillIds: resumeToEdit.technicalSkillIds || [],
        projectIds: resumeToEdit.projectIds || []
      });
      // If resumeToEdit has full technicalSkills/projects, use them
      if (resumeToEdit.technicalSkills && resumeToEdit.technicalSkills.length > 0) {
        setTechnicalSkills(resumeToEdit.technicalSkills);
      }
      if (resumeToEdit.projects && resumeToEdit.projects.length > 0) {
        setProjects(resumeToEdit.projects);
      }
    }
  }, [resumeToEdit]);

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => setShowSuccessModal(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal]);

  const fetchMasterData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [skillsResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/master-data/technical-skills`),
        axios.get(`${API_BASE_URL}/master-data/projects`)
      ]);
      setTechnicalSkills(skillsResponse.data);
      setProjects(projectsResponse.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      setError('Failed to load technical skills and projects. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateResumeData = (field, value) => {
    setResumeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkExperience = (experience) => {
    setResumeData(prev => ({
      ...prev,
      workExperiences: [...prev.workExperiences, experience]
    }));
  };

  const updateWorkExperience = (index, experience) => {
    setResumeData(prev => ({
      ...prev,
      workExperiences: prev.workExperiences.map((exp, i) => 
        i === index ? experience : exp
      )
    }));
  };

  const removeWorkExperience = (index) => {
    setResumeData(prev => ({
      ...prev,
      workExperiences: prev.workExperiences.filter((_, i) => i !== index)
    }));
  };

  const addEducation = (education) => {
    setResumeData(prev => ({
      ...prev,
      educations: [...prev.educations, education]
    }));
  };

  const updateEducation = (index, education) => {
    setResumeData(prev => ({
      ...prev,
      educations: prev.educations.map((edu, i) => 
        i === index ? education : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setResumeData(prev => ({
      ...prev,
      educations: prev.educations.filter((_, i) => i !== index)
    }));
  };

  const addCertification = (certification) => {
    setResumeData(prev => ({
      ...prev,
      certifications: [...prev.certifications, certification]
    }));
  };

  const updateCertification = (index, certification) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => 
        i === index ? certification : cert
      )
    }));
  };

  const removeCertification = (index) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const saveResume = async () => {
    try {
      setIsLoading(true);
      let response;
      
      if (resumeToEdit) {
        // Update existing resume
        response = await axios.put(`${API_BASE_URL}/resumes/${resumeToEdit.id}`, resumeData);
        setShowSuccessModal(true);
        setSuccessMessage('Resume updated successfully!');
      } else {
        // Create new resume
        response = await axios.post(`${API_BASE_URL}/resumes`, resumeData);
        setShowSuccessModal(true);
        setSuccessMessage('Resume saved successfully!');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error saving resume:', error);
      setError('Error saving resume. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadResume = (resume) => {
    setResumeData({
      name: resume.name || '',
      mobile: resume.mobile || '',
      email: resume.email || '',
      profileSummary: resume.profileSummary || '',
      linkedin: resume.linkedin || '',
      github: resume.github || '',
      workExperiences: resume.workExperiences || [],
      educations: resume.educations || [],
      certifications: resume.certifications || [],
      technicalSkillIds: resume.technicalSkillIds || [],
      projectIds: resume.projectIds || []
    });
    setIsResumeManagerMode(false);
    setCurrentStep(1);
  };

  const handleBackFromManager = () => {
    setIsResumeManagerMode(false);
  };

  const handleSkillAdded = (newSkill) => {
    setTechnicalSkills(prev => [...prev, newSkill]);
    setIsAddSkillModalOpen(false);
  };

  const handleProjectAdded = (newProject) => {
    setProjects(prev => [...prev, newProject]);
    setIsAddProjectModalOpen(false);
  };

  const handleSkillUpdated = (updatedSkill) => {
    setTechnicalSkills(prev => prev.map(skill =>
      skill.id === updatedSkill.id ? updatedSkill : skill
    ));
    setIsEditSkillModalOpen(false);
    setSkillToEdit(null);
  };

  const handleProjectUpdated = (updatedProject) => {
    setProjects(prev => prev.map(project =>
      project.id === updatedProject.id ? updatedProject : project
    ));
    setIsEditProjectModalOpen(false);
    setProjectToEdit(null);
  };

  const handleEditSkill = (skill) => {
    setSkillToEdit(skill);
    setIsEditSkillModalOpen(true);
  };

  const handleEditProject = (project) => {
    setProjectToEdit(project);
    setIsEditProjectModalOpen(true);
  };

  const handleEditEducation = (education, index) => {
    setEducationToEdit(education);
    setEditingIndex(index);
    setIsEditEducationModalOpen(true);
  };

  const handleEditWorkExperience = (experience, index) => {
    setExperienceToEdit(experience);
    setEditingIndex(index);
    setIsEditWorkExperienceModalOpen(true);
  };

  const handleEditCertification = (certification, index) => {
    setCertificationToEdit(certification);
    setEditingIndex(index);
    setIsEditCertificationModalOpen(true);
  };

  const downloadAsPDF = async () => {
    try {
      setIsLoading(true);
      console.log('Downloading PDF for resume ID:', resumeData.id);
      const response = await fetch(`${API_BASE_URL}/resume-pdf/${resumeData.id}`, {
        method: 'GET',
        headers: {
          // Add auth headers if needed
        }
      });
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resumeData.name.replace(/\s+/g, '_')}_Resume.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setShowSuccessModal(true);
      setSuccessMessage('Resume downloaded successfully!');
    } catch (err) {
      setError('Error downloading PDF: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAsWord = async () => {
    try {
      setIsLoading(true);
      
      const getSelectedSkillNames = () => {
        return technicalSkills
          .filter(skill => resumeData.technicalSkillIds.includes(skill.id))
          .map(skill => skill.name);
      };

      const getSelectedProjectNames = () => {
        return projects
          .filter(project => resumeData.projectIds.includes(project.id))
          .map(project => project.name);
      };

      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long' 
        });
      };

      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Header
            new Paragraph({
              text: resumeData.name,
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 }
            }),
            
            // Contact Info
            new Paragraph({
              children: [
                new TextRun({ text: resumeData.email, size: 20 }),
                new TextRun({ text: ' | ', size: 20 }),
                new TextRun({ text: resumeData.mobile, size: 20 }),
                ...(resumeData.linkedin ? [
                  new TextRun({ text: ' | ', size: 20 }),
                  new TextRun({ text: `LinkedIn: ${resumeData.linkedin}`, size: 20 })
                ] : []),
                ...(resumeData.github ? [
                  new TextRun({ text: ' | ', size: 20 }),
                  new TextRun({ text: `GitHub: ${resumeData.github}`, size: 20 })
                ] : [])
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // Profile Summary
            ...(resumeData.profileSummary ? [
              new Paragraph({
                text: 'PROFILE SUMMARY',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              new Paragraph({
                text: resumeData.profileSummary.replace(/<[^>]*>/g, ''),
                spacing: { after: 400 }
              })
            ] : []),

            // Technical Skills
            ...(resumeData.technicalSkillIds.length > 0 ? [
              new Paragraph({
                text: 'TECHNICAL SKILLS',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              new Paragraph({
                text: getSelectedSkillNames().join(', '),
                spacing: { after: 400 }
              })
            ] : []),

            // Work Experience
            ...(resumeData.workExperiences.length > 0 ? [
              new Paragraph({
                text: 'WORK EXPERIENCE',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              ...resumeData.workExperiences.flatMap(exp => [
                new Paragraph({
                  text: `${exp.jobTitle} at ${exp.companyName}`,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 100 }
                }),
                new Paragraph({
                  text: `${formatDate(exp.startDate)} - ${exp.endDate ? formatDate(exp.endDate) : 'Present'}`,
                  spacing: { after: 100 }
                }),
                new Paragraph({
                  text: exp.description.replace(/<[^>]*>/g, ''),
                  spacing: { after: 200 }
                })
              ])
            ] : []),

            // Education
            ...(resumeData.educations.length > 0 ? [
              new Paragraph({
                text: 'EDUCATION',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              ...resumeData.educations.flatMap(edu => [
                new Paragraph({
                  text: `${edu.degree} in ${edu.fieldOfStudy}`,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 100 }
                }),
                new Paragraph({
                  text: `${edu.institution} • ${formatDate(edu.graduationDate)}`,
                  spacing: { after: 200 }
                })
              ])
            ] : []),

            // Certifications
            ...(resumeData.certifications.length > 0 ? [
              new Paragraph({
                text: 'CERTIFICATIONS',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              ...resumeData.certifications.flatMap(cert => [
                new Paragraph({
                  text: cert.name,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 100 }
                }),
                new Paragraph({
                  text: `${cert.issuingOrganization} • ${formatDate(cert.issueDate)}`,
                  spacing: { after: 200 }
                })
              ])
            ] : []),

            // Projects
            ...(resumeData.projectIds.length > 0 ? [
              new Paragraph({
                text: 'PROJECTS',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 }
              }),
              ...getSelectedProjectNames().map(projectName => 
                new Paragraph({
                  text: projectName,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 200 }
                })
              )
            ] : [])
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${resumeData.name.replace(/\s+/g, '_')}_Resume.docx`);
    } catch (error) {
      console.error('Error generating Word document:', error);
      alert('Error generating Word document. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfo
            data={resumeData}
            updateData={updateResumeData}
          />
        );
      case 2:
        return (
          <TechnicalSkills
            selectedSkills={resumeData.technicalSkillIds}
            availableSkills={technicalSkills}
            onSkillsChange={(skillIds) => updateResumeData('technicalSkillIds', skillIds)}
            onAddNewSkill={() => setIsAddSkillModalOpen(true)}
            onSkillsRefresh={fetchMasterData}
            onEditSkill={handleEditSkill}
          />
        );
      case 3:
        return (
          <WorkExperience
            experiences={resumeData.workExperiences}
            onRemove={removeWorkExperience}
            onEdit={handleEditWorkExperience}
            onAddNew={() => setIsAddWorkExperienceModalOpen(true)}
          />
        );
      case 4:
        return (
          <Education
            educations={resumeData.educations}
            onRemove={removeEducation}
            onEdit={handleEditEducation}
            onAddNew={() => setIsAddEducationModalOpen(true)}
          />
        );
      case 5:
        return (
          <Certifications
            certifications={resumeData.certifications}
            onRemove={removeCertification}
            onEdit={handleEditCertification}
            onAddNew={() => setIsAddCertificationModalOpen(true)}
          />
        );
      case 6:
        return (
          <Projects
            selectedProjects={resumeData.projectIds}
            availableProjects={projects}
            onProjectsChange={(projectIds) => updateResumeData('projectIds', projectIds)}
            onAddNewProject={() => setIsAddProjectModalOpen(true)}
            onProjectsRefresh={fetchMasterData}
            onEditProject={handleEditProject}
          />
        );
      default:
        return null;
    }
  };

  const handleEducationAdded = (education) => {
    addEducation(education);
    setIsAddEducationModalOpen(false);
  };

  const handleWorkExperienceAdded = (experience) => {
    addWorkExperience(experience);
    setIsAddWorkExperienceModalOpen(false);
  };

  const handleCertificationAdded = (certification) => {
    addCertification(certification);
    setIsAddCertificationModalOpen(false);
  };

  if (isPreviewMode) {
    return (
      <div className="resume-builder-container">
        {/* Resume Preview, pass selectedTemplate */}
        <ResumePreview
          resumeData={resumeData}
          template={selectedTemplate}
          technicalSkills={technicalSkills}
          projects={projects}
          certifications={resumeData.certifications}
          onBack={() => setIsPreviewMode(false)}
          onSave={saveResume}
          onDownloadPDF={downloadAsPDF}
          onDownloadWord={downloadAsWord}
          isLoading={isLoading}
        />
      </div>
    );
  }

  if (isResumeManagerMode) {
    return (
      <ResumeManager
        onLoadResume={handleLoadResume}
        onBack={handleBackFromManager}
      />
    );
  }

  return (
    <div className="resume-builder">
      <div className="container">
        <div className="card">
          {/* Header */}
          <div className="header">
            <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              {onBackToList && (
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <button
                    onClick={onBackToList}
                    className="btn btn-secondary"
                  >
                    ← Back to Resume List
                  </button>
                </div>
              )}
              <div style={{ textAlign: 'right', flex: 1, marginLeft: 'auto' }}>
                <h1>{headerTitle || (resumeToEdit ? 'Edit Resume' : 'Professional Resume Builder')}</h1>
              </div>
            </div>
            {resumeToEdit && (
              <div className="edit-mode-indicator">
                <span>📝 Editing: {resumeToEdit.name}</span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="loading">
              <div className="spinner"></div>
              <span>Loading...</span>
            </div>
          )}
          
          {/* Progress Bar */}
          <div className="progress-container">
            <div className="step-indicators">
              {[1, 2, 3, 4, 5, 6].map(step => (
                <div
                  key={step}
                  className={`step-indicator ${
                    step <= currentStep ? 'step-active' : 'step-inactive'
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(currentStep / 6) * 100}%` }}
              ></div>
            </div>
            <div className="step-labels">
              <span>Personal Info</span>
              <span>Skills</span>
              <span>Experience</span>
              <span>Education</span>
              <span>Certifications</span>
              <span>Projects</span>
            </div>
          </div>

          {/* Step Content */}
          <div>
            {renderStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`btn btn-secondary ${currentStep === 1 ? 'disabled' : ''}`}
            >
              ← Previous
            </button>
            
            <div className="nav-buttons">
              {/* <button
                onClick={() => setIsResumeManagerMode(true)}
                className="btn btn-secondary"
                style={{ marginRight: '0.5rem' }}
              >
                📁 Manage Resumes
              </button> */}
              
              <button
                onClick={() => setIsPreviewMode(true)}
                disabled={!resumeData.name || !resumeData.email}
                className={`btn btn-success ${!resumeData.name || !resumeData.email ? 'disabled' : ''}`}
              >
                👁️ Preview Resume
              </button>
              
              {currentStep < 6 ? (
                <button
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={saveResume}
                  disabled={isLoading}
                  className="btn btn-primary"
                >
                  {isLoading ? 'Saving...' : resumeToEdit ? '💾 Update Resume' : '💾 Save Resume'}
                </button>
              )}
            </div>
          </div>

          {/* Progress Info */}
          <div className="progress-info">
            Step {currentStep} of 6 • {Math.round((currentStep / 6) * 100)}% Complete
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddSkillModal
        isOpen={isAddSkillModalOpen}
        onClose={() => setIsAddSkillModalOpen(false)}
        onSkillAdded={handleSkillAdded}
      />
      
      <AddProjectModal
        isOpen={isAddProjectModalOpen}
        onClose={() => setIsAddProjectModalOpen(false)}
        onProjectAdded={handleProjectAdded}
      />

      <EditSkillModal
        isOpen={isEditSkillModalOpen}
        onClose={() => setIsEditSkillModalOpen(false)}
        skillToEdit={skillToEdit}
        onSkillUpdated={handleSkillUpdated}
      />

      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        onClose={() => setIsEditProjectModalOpen(false)}
        projectToEdit={projectToEdit}
        onProjectUpdated={handleProjectUpdated}
      />

      <EditEducationModal
        isOpen={isEditEducationModalOpen}
        onClose={() => setIsEditEducationModalOpen(false)}
        educationToEdit={educationToEdit}
        editingIndex={editingIndex}
        onEducationUpdated={(index, updatedEducation) => {
          updateEducation(index, updatedEducation);
          setIsEditEducationModalOpen(false);
        }}
      />

      <EditWorkExperienceModal
        isOpen={isEditWorkExperienceModalOpen}
        onClose={() => setIsEditWorkExperienceModalOpen(false)}
        experienceToEdit={experienceToEdit}
        editingIndex={editingIndex}
        onExperienceUpdated={(index, updatedExperience) => {
          updateWorkExperience(index, updatedExperience);
          setIsEditWorkExperienceModalOpen(false);
        }}
      />

      <EditCertificationModal
        isOpen={isEditCertificationModalOpen}
        onClose={() => setIsEditCertificationModalOpen(false)}
        certificationToEdit={certificationToEdit}
        editingIndex={editingIndex}
        onCertificationUpdated={(index, updatedCertification) => {
          updateCertification(index, updatedCertification);
          setIsEditCertificationModalOpen(false);
        }}
      />

      <AddEducationModal
        isOpen={isAddEducationModalOpen}
        onClose={() => setIsAddEducationModalOpen(false)}
        onEducationAdded={handleEducationAdded}
      />

      <AddWorkExperienceModal
        isOpen={isAddWorkExperienceModalOpen}
        onClose={() => setIsAddWorkExperienceModalOpen(false)}
        onExperienceAdded={handleWorkExperienceAdded}
      />

      <AddCertificationModal
        isOpen={isAddCertificationModalOpen}
        onClose={() => setIsAddCertificationModalOpen(false)}
        onCertificationAdded={handleCertificationAdded}
      />

      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    </div>
  );
};

export default ResumeBuilder; 